import { defineStore } from 'pinia'
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    User as FirebaseUser
} from 'firebase/auth'
import { doc, setDoc, getDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { auth, db } from '@/services/firebase'
import type { User } from '@/types/user'
import { notificationService } from '@/services/notificationService'
import { pushNotificationService } from '@/services/pushNotificationService'
import {
    buildUserRegistrationDoc,
    registrationNotificationMessage,
    REGISTRATION_NOTIFICATION_TITLE,
    type RegisterInput
} from '@/utils/registrationForm'

// Pinia state'inde function tutulamaz; listener'ı modül seviyesinde saklıyoruz
let userDocUnsubscribe: Unsubscribe | null = null

function stopUserDocListener() {
    if (userDocUnsubscribe) {
        userDocUnsubscribe()
        userDocUnsubscribe = null
    }
}

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    loading: boolean
    error: string | null
    initialized: boolean
    // Geçici şifreyle giriş yapıldığında true olur; kullanıcı kalıcı şifresini
    // belirleyene kadar zorunlu şifre değiştirme ekranı gösterilir.
    mustResetPassword: boolean
    // Geçici şifreyle giriş yapan kullanıcının telefonu (kaydı temizlemek için)
    resetPhone: string | null
}

// Helper: telefon numarasından dummy email oluştur
function phoneToEmail(phoneNumber: string): string {
    return `${phoneNumber}@tennis.local`
}

export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        initialized: false,
        mustResetPassword: false,
        resetPhone: null
    }),

    getters: {
        // Patron (boss) admin'in tüm yetkilerine sahiptir: kortlarda kimin
        // olduğunu görür, slot iptal eder, fiyat/yoklama düzenler. Bu yüzden
        // isAdmin boss'u da kapsar; boss login sonrası varsayılan admin panelini
        // görür (izleme panelini menüden açar). Saf admin gerekirse isBoss ile ayır.
        isAdmin: (state) => state.user?.role === 'admin' || state.user?.role === 'boss',
        isStudent: (state) => state.user?.role === 'student',
        isBoss: (state) => state.user?.role === 'boss'
    },

    actions: {
        async login(phoneNumber: string, password: string) {
            this.loading = true
            this.error = null
            this.mustResetPassword = false
            this.resetPhone = null

            const dummyEmail = phoneToEmail(phoneNumber)
            console.log('🔐 Giriş yapılıyor:', phoneNumber)

            try {
                // Geçici şifre artık admin tarafından (setTempPassword Cloud Function ile)
                // doğrudan gerçek Auth şifresine yazılıyor; bu yüzden geçici şifreyle de
                // normal giriş çalışır. Kalıcı şifre belirleme zorunluluğu, kullanıcı
                // dokümanındaki mustResetPassword bayrağıyla yönetilir (ForcePasswordReset.vue).
                const userCredential = await signInWithEmailAndPassword(auth, dummyEmail, password)
                console.log('✅ Firebase auth başarılı, UID:', userCredential.user.uid)

                await this.fetchUserData(userCredential.user.uid)
                console.log('✅ Kullanıcı verisi yüklendi:', this.user)

                // Firestore'daki kalıcı bayrağı login akışına da yansıt
                if (this.user?.mustResetPassword === true) {
                    this.mustResetPassword = true
                    this.resetPhone = phoneNumber
                }

                return true
            } catch (error: any) {
                console.error('❌ Giriş hatası:', error)
                this.error = this.getErrorMessage(error)
                return false
            } finally {
                this.loading = false
            }
        },

        // Geçici şifreyle giriş yapan kullanıcı kalıcı şifresini belirler.
        // Gerçek Firebase Auth şifresi burada güncellenir, geçici kayıt silinir.
        async completePasswordReset(newPassword: string) {
            const { updatePassword } = await import('firebase/auth')
            const { clearMustResetPassword } = await import('@/services/passwordResetService')

            const currentUser = auth.currentUser
            if (!currentUser) {
                throw new Error('Oturum bulunamadı, lütfen tekrar giriş yapın.')
            }

            try {
                // Gerçek Auth şifresini güncelle. Geçici şifreyle yeni giriş yapıldıysa
                // reauthenticate gerekmez; ancak sayfa yenilenip oturum eskidiyse Firebase
                // 'requires-recent-login' döndürebilir — bunu kullanıcıya açıkça bildiriyoruz.
                await updatePassword(currentUser, newPassword)
            } catch (error: any) {
                if (error?.code === 'auth/requires-recent-login') {
                    throw new Error('Güvenlik nedeniyle lütfen çıkış yapıp geçici şifrenizle tekrar giriş yapın, ardından yeni şifrenizi belirleyin.')
                }
                throw new Error(this.getErrorMessage(error))
            }

            // mustResetPassword bayrağını kaldır
            await clearMustResetPassword(currentUser.uid)

            this.mustResetPassword = false
            this.resetPhone = null
        },

        async register(userData: RegisterInput) {
            this.loading = true
            this.error = null

            try {
                // NOT: Eskiden burada GİRİŞ YAPMADAN users koleksiyonu sorgulanıyordu
                // ("bu telefon daha önce silinmiş mi?"). Kurallar girişsiz okumaya izin
                // vermediği için sorgu reddediliyor ve kayıt her seferinde hata veriyordu.
                // Silme akışı Auth kaydını siler ve telefonu temizler; Auth kaydı hâlâ
                // duruyorsa email-already-in-use yolu (resumeExistingAccount) durumu ele alır.
                const dummyEmail = phoneToEmail(userData.phone_number)
                console.log('📝 Kullanıcı kaydediliyor:', userData.phone_number)
                const userCredential = await createUserWithEmailAndPassword(auth, dummyEmail, userData.password)
                await this.completeRegistration(userCredential.user.uid, userData)
                return true
            } catch (error: any) {
                if (error?.code === 'auth/email-already-in-use') {
                    return await this.resumeExistingAccount(userData)
                }
                console.error('❌ Kayıt hatası:', error)
                this.error = this.getErrorMessage(error)
                return false
            } finally {
                this.loading = false
            }
        },

        // Kayıt belgesini yazar, admin/boss'a bildirim gönderir; oturum açık kalır
        // (öğrenci panelde "Hesap Onayı Bekleniyor" uyarısını görür).
        async completeRegistration(uid: string, userData: RegisterInput) {
            const user = buildUserRegistrationDoc(uid, userData, new Date())
            console.log('💾 Firestore\'a kullanıcı verisi yazılıyor...')
            await setDoc(doc(db, 'users', uid), user)

            if (user.role === 'student' && user.status === 'pending') {
                // Bildirim yazılamasa da kayıt geçerlidir: admin bekleyen öğrencileri
                // users koleksiyonundan da görür (Notifications.vue sentetik kayıtları).
                try {
                    await notificationService.createAdminNotification(
                        REGISTRATION_NOTIFICATION_TITLE,
                        registrationNotificationMessage(user),
                        'approval_pending',
                        uid
                    )
                } catch (notifyError) {
                    console.warn('Kayıt bildirimi yazılamadı:', notifyError)
                }
            }

            this.user = user
            this.isAuthenticated = true
            // Canlı dinleyici belge yazılmadan önce "belge yok" görüp hata yazmış olabilir.
            this.error = null
            console.log('✅ Kayıt başarılı:', uid)
        },

        // Telefonun Auth hesabı zaten var. Girilen şifreyle giriş yapılabilirse: belge
        // yoksa (kayıt reddedilmiş) yeniden yazılır; belge varsa kayıt yapılmaz, oturum
        // kapatılır ve kullanıcı yönlendirilir.
        async resumeExistingAccount(userData: RegisterInput): Promise<boolean> {
            const alreadyRegistered = 'Bu telefon numarası zaten kayıtlı. Giriş sayfasından giriş yapabilirsiniz.'
            let uid: string
            try {
                const credential = await signInWithEmailAndPassword(auth, phoneToEmail(userData.phone_number), userData.password)
                uid = credential.user.uid
            } catch (signInError) {
                console.warn('Mevcut hesaba girilen şifreyle girilemedi:', signInError)
                this.error = alreadyRegistered
                return false
            }

            try {
                const existing = await getDoc(doc(db, 'users', uid))
                if (!existing.exists()) {
                    console.log('📝 Belge yok (kayıt reddedilmiş) — yeniden oluşturuluyor')
                    await this.completeRegistration(uid, userData)
                    return true
                }
                const data = existing.data() as Partial<User>
                await this.logout()
                this.error = (data.deleted === true || data.status === 'deleted')
                    ? 'Bu telefon numarasıyla açılmış hesap silinmiş. Yeniden kayıt için lütfen yöneticiyle iletişime geçin.'
                    : alreadyRegistered
                return false
            } catch (error: any) {
                console.error('❌ Kayıt kurtarma hatası:', error)
                this.error = this.getErrorMessage(error)
                return false
            }
        },

        async logout() {
            try {
                console.log('🚪 Çıkış yapılıyor...')
                stopUserDocListener()
                await signOut(auth)
                this.user = null
                this.isAuthenticated = false
                console.log('✅ Çıkış başarılı')
            } catch (error: any) {
                console.error('❌ Çıkış hatası:', error)
                this.error = this.getErrorMessage(error)
            }
        },

        async sendPasswordResetEmail(phoneNumber: string) {
            this.loading = true
            this.error = null

            try {
                const dummyEmail = phoneToEmail(phoneNumber)
                await sendPasswordResetEmail(auth, dummyEmail)
                return true
            } catch (error: any) {
                this.error = this.getErrorMessage(error)
                throw new Error(this.error)
            } finally {
                this.loading = false
            }
        },

        async fetchUserData(uid: string) {
            // Önceki listener varsa kapat (kullanıcı değiştiyse)
            stopUserDocListener()

            console.log('🔍 Firestore\'da kullanıcı verisi için canlı dinleyici kuruluyor, UID:', uid)

            return new Promise<void>((resolve) => {
                let resolved = false

                userDocUnsubscribe = onSnapshot(
                    doc(db, 'users', uid),
                    async (userDoc) => {
                        try {
                            if (userDoc.exists()) {
                                const userData = userDoc.data() as User
                                console.log('🔄 Kullanıcı verisi güncellendi (snapshot):', {
                                    id: userData.id,
                                    phone_number: userData.phone_number,
                                    firstName: userData.firstName,
                                    role: userData.role,
                                    membershipType: (userData as any).membershipType,
                                    groupAssignment: (userData as any).groupAssignment
                                })

                                // Silinmiş hesap savunması: öğrenci silindiğinde Firestore
                                // dokümanı kalır (soft delete) ama deleted=true / status='deleted'
                                // olur. Auth kaydı Cloud Function ile silinse bile, açık bir oturum
                                // ya da eski cihaz buraya düşebilir. Bu canlı snapshot guard'ı
                                // oturumu anında kapatıp girişi engeller (ikinci savunma katmanı).
                                if (userData.deleted === true || userData.status === 'deleted') {
                                    console.warn('⛔ Silinmiş hesap girişi engellendi:', uid)
                                    stopUserDocListener()
                                    await signOut(auth)
                                    this.user = null
                                    this.isAuthenticated = false
                                    this.error = 'Bu hesap silinmiş. Lütfen yöneticiyle iletişime geçin.'
                                    return
                                }

                                if (userData.role === 'student' && userData.status === 'pending') {
                                    console.log('⚠️ Kullanıcı onay bekliyor, ancak sisteme girişine izin veriliyor (dashboard kilitli).', uid)
                                }

                                this.user = {
                                    ...userData,
                                    id: uid
                                }
                                this.isAuthenticated = true
                            } else {
                                // Doküman "yok" görünüyor. ESKİDEN burada otomatik olarak
                                // role='student'/'Kullanıcı' bir doküman YAZILIYORDU. Bu çok
                                // tehlikeliydi: geçici bir okuma hatası ya da kural gecikmesi
                                // anlık "yok" döndürdüğünde, GERÇEK bir kullanıcının (örn.
                                // boss/admin) dokümanını üzerine yazıp student'a çeviriyordu.
                                // Bir kez boss hesabını bu şekilde yok etti.
                                //
                                // Doğrusu: kayıt (register) zaten kendi dokümanını oluşturur;
                                // giriş yapan birinin dokümanı yoksa, sessizce yeni doküman
                                // YAZMA — oturumu açma ve net bir hata bildir. Asla mevcut
                                // veriyi ezme.
                                console.error('❌ Firestore\'da kullanıcı dokümanı bulunamadı (otomatik oluşturma DEVRE DIŞI), UID:', uid)
                                this.user = null
                                this.isAuthenticated = false
                                this.error = 'Hesap kaydınız bulunamadı. Lütfen yöneticiyle iletişime geçin.'
                            }
                        } catch (error: any) {
                            console.error('❌ Kullanıcı snapshot işleme hatası:', error)
                            this.error = this.getErrorMessage(error)
                        } finally {
                            if (!resolved) {
                                resolved = true
                                resolve()
                            }
                        }
                    },
                    (error) => {
                        console.error('❌ Kullanıcı verisi dinleyici hatası:', error)
                        this.error = this.getErrorMessage(error)
                        this.user = null
                        this.isAuthenticated = false
                        if (!resolved) {
                            resolved = true
                            resolve()
                        }
                    }
                )
            })
        },

        initializeAuth() {
            console.log('🚀 Auth state başlatılıyor...')

            return new Promise<void>((resolve) => {
                const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
                    console.log('🔄 Auth state değişti:', firebaseUser ? `Kullanıcı: ${firebaseUser.email}` : 'Çıkış yapılmış')

                    if (firebaseUser) {
                        console.log('👤 Firebase kullanıcısı mevcut:', {
                            uid: firebaseUser.uid,
                            email: firebaseUser.email
                        })

                        try {
                            await this.fetchUserData(firebaseUser.uid)
                            console.log('✅ Kullanıcı verisi başarıyla yüklendi')

                            pushNotificationService.registerDeviceToken(firebaseUser.uid).catch(() => {})
                        } catch (error) {
                            console.error('❌ Kullanıcı verisi yüklenemedi:', error)
                        }
                    } else {
                        console.log('👤 Kullanıcı oturumu kapalı')
                        stopUserDocListener()
                        if (this.user?.id) { pushNotificationService.unregisterDeviceToken(this.user.id).catch(() => {}) }
                        this.user = null
                        this.isAuthenticated = false
                    }

                    // Mark as initialized regardless of auth state
                    this.initialized = true
                    console.log('✅ Auth state hazır. Kullanıcı:', this.user ? `${this.user.firstName} ${this.user.lastName}` : 'Yok')
                    resolve()
                }, (error) => {
                    console.error('❌ Auth state listener hatası:', error)
                    this.initialized = true
                    resolve()
                })
            })
        },

        async waitForAuth(): Promise<void> {
            console.log('⏳ Auth state bekleniyor, initialized:', this.initialized)

            if (this.initialized) {
                console.log('✅ Auth state zaten hazır')
                return Promise.resolve()
            }

            return new Promise<void>((resolve) => {
                console.log('🔄 Auth state değişimi bekleniyor...')
                const unwatch = this.$subscribe((mutation, state) => {
                    if (state.initialized) {
                        console.log('✅ Auth state hazır oldu')
                        unwatch()
                        resolve()
                    }
                })

                // Add timeout to prevent infinite waiting
                setTimeout(() => {
                    if (!this.initialized) {
                        console.warn('⚠️ Auth state timeout, forcing initialization')
                        this.initialized = true
                        unwatch()
                        resolve()
                    }
                }, 5000) // 5 second timeout
            })
        },

        getErrorMessage(error: any): string {
            const code = error.code || error.message

            switch (code) {
                case 'auth/user-not-found':
                    return 'Bu telefon numarasıyla kayıtlı kullanıcı bulunamadı'
                case 'auth/wrong-password':
                    return 'Yanlış şifre girdiniz'
                case 'auth/email-already-in-use':
                    return 'Bu telefon numarası zaten kullanımda'
                case 'auth/weak-password':
                    return 'Şifre çok zayıf. Daha güçlü bir şifre seçin'
                case 'auth/invalid-email':
                    return 'Geçersiz telefon numarası'
                case 'auth/user-disabled':
                    return 'Bu hesap devre dışı bırakılmış'
                case 'auth/too-many-requests':
                    return 'Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin'
                case 'auth/network-request-failed':
                    return 'Ağ bağlantısı hatası. İnternet bağlantınızı kontrol edin'
                case 'auth/requires-recent-login':
                    return 'Bu işlem için tekrar giriş yapmanız gerekiyor'
                case 'auth/invalid-credential':
                    return 'Geçersiz kimlik bilgileri'
                case 'pending_approval':
                    return 'Hesabınız onay bekliyor. Yöneticinin hesabınızı onaylaması gerekmektedir.'
                default:
                    return error.message || 'Bilinmeyen bir hata oluştu'
            }
        },

        clearError() {
            this.error = null
        }
    }
})